/*
 * ตาราง: where each cell and its text go. Plain arithmetic — the text's height comes from `measure` (in the app: a
 * real Thai text box with the real font, src/table/tableFabric.ts), so this is tested without a canvas.
 */
import { hasBold } from '../fonts/catalog';
import { mergeAt, type TableCell, type TableData } from './tableModel';

export interface MeasureOpts { font: string; weight: 400 | 700; size: number; width: number; align: 'left' | 'center' | 'right' }
/** the height of `text` laid out in a box `width` wide (Thai line breaking, the table's line height) */
export type Measure = (text: string, o: MeasureOpts) => number;

export interface LaidCell {
  r: number; c: number; rs: number; cs: number;
  /** the cell's box, page px from the table's top-left */
  x: number; y: number; w: number; h: number;
  cell: TableCell;
  /** where its text goes (null = no text) */
  text: null | { x: number; y: number; w: number; h: number; opts: MeasureOpts };
}
export interface TableLayout { w: number; h: number; colX: number[]; rowY: number[]; rowH: number[]; cells: LaidCell[] }

const ALIGN = { l: 'left', c: 'center', r: 'right' } as const;

export function textOpts(t: TableData, cell: TableCell, width: number): MeasureOpts {
  return {
    font: t.font,
    // a font without a real bold stays regular (the screen and the print PDF the same — as the text toolbar does)
    weight: cell.b && hasBold(t.font) ? 700 : 400,
    size: cell.fs ?? t.size,
    width: Math.max(1, width),
    align: ALIGN[cell.al ?? 'l']
  };
}

export function layoutTable(t: TableData, measure: Measure): TableLayout {
  const colX = [0];
  for (const w of t.cols) colX.push(colX[colX.length - 1] + w);
  const W = colX[colX.length - 1];
  // the least height of a row: one line of the table's text (an empty row does not shrink to nothing), or its own
  const oneLine = measure('ก', { font: t.font, weight: 400, size: t.size, width: 10000, align: 'left' }) + 2 * t.pad;
  const rowH = t.rows.map(h => Math.max(h, oneLine));
  const shown: Array<Omit<LaidCell, 'x' | 'y' | 'h' | 'text'> & { need: number; textH: number; opts: MeasureOpts | null }> = [];
  for (let r = 0; r < t.rows.length; r++) {
    for (let c = 0; c < t.cols.length; c++) {
      const m = mergeAt(t, r, c);
      if (m && (m.r !== r || m.c !== c)) continue;
      const rs = m?.rs ?? 1, cs = m?.cs ?? 1;
      const cell = t.cells[r][c];
      const w = colX[c + cs] - colX[c];
      const opts = cell.t.trim() ? textOpts(t, cell, w - 2 * t.pad) : null;
      const textH = opts ? measure(cell.t, opts) : 0;
      shown.push({ r, c, rs, cs, w, cell, need: textH + 2 * t.pad, textH, opts });
    }
  }
  // one-row cells first; then a merged cell taller than its rows makes its last row taller
  for (const s of shown) if (s.rs === 1) rowH[s.r] = Math.max(rowH[s.r], s.need);
  for (const s of [...shown].filter(x => x.rs > 1).sort((a, b) => (a.r + a.rs) - (b.r + b.rs))) {
    const have = rowH.slice(s.r, s.r + s.rs).reduce((a, b) => a + b, 0);
    if (s.need > have) rowH[s.r + s.rs - 1] += s.need - have;
  }
  const rowY = [0];
  for (const h of rowH) rowY.push(rowY[rowY.length - 1] + h);
  const cells: LaidCell[] = shown.map(s => {
    const x = colX[s.c], y = rowY[s.r], h = rowY[s.r + s.rs] - y;
    let text: LaidCell['text'] = null;
    if (s.opts) {
      const va = s.cell.va ?? 't';
      const ty = va === 'm' ? y + (h - s.textH) / 2 : va === 'b' ? y + h - t.pad - s.textH : y + t.pad;
      text = { x: x + t.pad, y: ty, w: s.opts.width, h: s.textH, opts: s.opts };
    }
    return { r: s.r, c: s.c, rs: s.rs, cs: s.cs, x, y, w: s.w, h, cell: s.cell, text };
  });
  return { w: W, h: rowY[rowY.length - 1], colX, rowY, rowH, cells };
}
